import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { dataKeys, getJobs, type DatasetLocation } from "./api";
import type { JobHistoryItem, Page } from "./types";


export const DATA_MANAGEMENT_ACTIVE_POLL_MS = configuredPollDelay();
export const DATA_MANAGEMENT_MAX_POLL_MS = 15_000;

export const ACTIVE_JOB_STATES = new Set([
  "queued",
  "running",
  "retry_wait",
  "cancel_requested",
  "cancelling",
]);

export const TERMINAL_JOB_STATES = new Set([
  "succeeded",
  "completed",
  "failed",
  "cancelled",
  "superseded",
]);

export function useDatasetDataManagementLiveState({
  location,
  onNotification,
}: {
  location: DatasetLocation;
  onNotification: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const previousStates = useRef(new Map<string, string>());
  const observedJobs = useRef(false);
  const notifiedTransitions = useRef(new Set<string>());
  const refreshInFlight = useRef<Promise<void> | null>(null);
  const [reconnecting, setReconnecting] = useState(false);

  const refreshDataManagementState = useCallback(async () => {
    if (refreshInFlight.current) return await refreshInFlight.current;
    const refresh = queryClient.invalidateQueries(
      { queryKey: dataKeys.root(location) },
      { cancelRefetch: false },
    );
    refreshInFlight.current = refresh;
    try {
      await refresh;
    } finally {
      refreshInFlight.current = null;
    }
  }, [location.clientId, location.datasetId, queryClient]);

  const jobsQuery = useQuery({
    queryKey: dataKeys.jobs(location),
    queryFn: () => getJobs(location),
    staleTime: 0,
    retry: false,
  });

  const activeJobs = useMemo(() => {
    return jobsQuery.data?.items.filter((job) => ACTIVE_JOB_STATES.has(job.state)) ?? [];
  }, [jobsQuery.data]);

  useEffect(() => {
    if (!jobsQuery.data) return;
    setReconnecting(false);
    const nextStates = new Map(jobsQuery.data.items.map((job) => [job.id, job.state]));
    if (!observedJobs.current) {
      previousStates.current = nextStates;
      observedJobs.current = true;
      return;
    }

    const terminalJobs = jobsQuery.data.items.filter((job) => {
      const previous = previousStates.current.get(job.id);
      if (previous === job.state) return false;
      if (!TERMINAL_JOB_STATES.has(job.state)) return false;
      const transition = `${job.id}:${job.state}`;
      if (notifiedTransitions.current.has(transition)) return false;
      notifiedTransitions.current.add(transition);
      return previous === undefined || ACTIVE_JOB_STATES.has(previous);
    });
    previousStates.current = nextStates;
    if (!terminalJobs.length) return;

    void refreshDataManagementState().finally(() => {
      onNotification(notificationForJobs(terminalJobs));
    });
  }, [jobsQuery.data, onNotification, refreshDataManagementState]);

  useEffect(() => {
    if (!activeJobs.length) return;
    let disposed = false;
    let timer = 0;
    let failures = 0;
    let polling = false;
    const schedule = () => {
      if (disposed || document.visibilityState === "hidden") return;
      timer = window.setTimeout(() => { void poll(); }, pollingDelay(jobsQuery.data, failures) || DATA_MANAGEMENT_ACTIVE_POLL_MS);
    };
    const poll = async () => {
      if (disposed || polling) return;
      polling = true;
      const result = await jobsQuery.refetch({ cancelRefetch: false });
      polling = false;
      if (disposed) return;
      if (result.error) {
        failures += 1;
        setReconnecting(true);
      } else {
        failures = 0;
        setReconnecting(false);
      }
      schedule();
    };
    const handleVisibility = () => {
      window.clearTimeout(timer);
      if (document.visibilityState !== "visible") return;
      schedule();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    schedule();
    return () => {
      disposed = true;
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [activeJobs.length, jobsQuery.data, jobsQuery.refetch]);

  useEffect(() => {
    const synchronizeVisibleTab = () => {
      if (document.visibilityState !== "visible") return;
      void refreshDataManagementState();
    };
    document.addEventListener("visibilitychange", synchronizeVisibleTab);
    return () => document.removeEventListener("visibilitychange", synchronizeVisibleTab);
  }, [refreshDataManagementState]);

  return {
    jobsQuery,
    activeJobs,
    reconnecting,
    refreshDataManagementState,
  };
}

export function pollingDelay(page: Page<JobHistoryItem> | undefined, failureCount: number) {
  if (!page?.items.some((job) => ACTIVE_JOB_STATES.has(job.state))) return false;
  if (document.visibilityState === "hidden") return false;
  if (failureCount <= 0) return DATA_MANAGEMENT_ACTIVE_POLL_MS;
  return Math.min(DATA_MANAGEMENT_ACTIVE_POLL_MS * (2 ** failureCount), DATA_MANAGEMENT_MAX_POLL_MS);
}

function notificationForJobs(jobs: JobHistoryItem[]) {
  const job = jobs[0];
  if (job.state === "cancelled") return "Le traitement a été annulé.";
  if (job.state === "failed") {
    if (job.type === "validate") return "La validation a échoué — consultez le rapport.";
    return `${jobLabel(job.type)} a échoué.`;
  }
  if (job.state === "superseded") return "Le traitement a été remplacé par une demande plus récente.";
  return `${jobLabel(job.type)} terminée.`;
}

function jobLabel(type: string) {
  if (type === "validate") return "Validation";
  if (type === "semanticize") return "Sémantisation";
  if (type === "publish") return "Publication";
  if (type === "materialize") return "Matérialisation";
  return "Opération";
}

function configuredPollDelay() {
  const value = Number(import.meta.env.VITE_DATA_MANAGEMENT_POLL_MS);
  if (!Number.isFinite(value) || value < 500) return 2_000;
  return value;
}
